import { ref, computed } from 'vue';
import get from 'lodash/get';

export function useSelectionV2(props, slots, emit, rowCanExpand) {
  const selectedRowKeys = ref({});
  const selectedRowData = ref([]);
  const selectedSubRowKeys = ref({});
  const selectedSubRowData = ref([]);

  const selectSubRows = (rowData) => {
    const subRowKeysSelected = {};
    const subRowDataSelected = [];

    if (props.subRowsTableSelectable && rowCanExpand(rowData)) {
      rowData.subRows.forEach((subRowData) => {
        const subRowKey = get(subRowData, props.subRowKey);
        subRowKeysSelected[subRowKey] = true;
        subRowDataSelected.push(subRowData);
      });
    }

    return {
      subRowKeys: subRowKeysSelected,
      subRowData: subRowDataSelected,
    };
  };
  const preSelectRows = () => {
    const hasPreSelectedKeys = props.selectedRowKeys && Object.keys(props.selectedRowKeys).length > 0;
    const hasPreSelectedSubRowKeys = props.selectedSubRowKeys && Object.keys(props.selectedSubRowKeys).length > 0;

    if (hasPreSelectedKeys || hasPreSelectedSubRowKeys) {
      const rowKeysSelected = {};
      let subRowKeysSelected = {};
      const subRowDataSelected = [];
      const rowDataSelected = [];

      props.data.forEach((rowData) => {
        const rowKey = get(rowData, props.rowKey);

        if (props.selectedRowKeys[rowKey]) {
          // If the row is pre-selected, add it to the selected rows
          rowKeysSelected[rowKey] = true;
          rowDataSelected.push(rowData);

          // Select sub-rows if top row pre-selected
          const { subRowKeys, subRowData } = selectSubRows(rowData);

          if (subRowData?.length > 0) {
            subRowKeysSelected = {
              ...subRowKeysSelected,
              ...subRowKeys,
            };
            subRowDataSelected.push(...subRowData);
          }
        }

        if (props.subRowsTableSelectable && hasPreSelectedSubRowKeys && rowCanExpand(rowData)) {
          rowData.subRows?.forEach((subRowData) => {
            const subRowKey = get(subRowData, props.subRowKey);

            // If the sub-row is pre-selected and it has not yet been selected, add it to the selected sub-rows
            if (props.selectedSubRowKeys[subRowKey] && !subRowKeysSelected[subRowKey]) {
              subRowKeysSelected[subRowKey] = true;
              subRowDataSelected.push(subRowData);

              // If the sub-row is pre-selected, ensure the parent row is also selected
              if (props.overrideParentOnOff && !rowKeysSelected[rowKey]) {
                rowKeysSelected[rowKey] = true;
                rowDataSelected.push(rowData);
              }
            }
          });
        }
      });

      selectedRowKeys.value = rowKeysSelected;
      selectedRowData.value = rowDataSelected;
      selectedSubRowKeys.value = subRowKeysSelected;
      selectedSubRowData.value = subRowDataSelected;
    }
  };
  preSelectRows();

  const emitRowUpdate = () => {
    emit('update:selected-row-keys', selectedRowKeys.value);
    emit('update:selected-row-data', selectedRowData.value);
  };
  const emitSubRowUpdate = () => {
    emit('update:selected-sub-row-keys', selectedSubRowKeys.value);
    emit('update:selected-sub-row-data', selectedSubRowData.value);
  };
  const isSubRowSelected = (rowData) => {
    const subRowKey = get(rowData, props.subRowKey);
    return !!selectedSubRowKeys.value[subRowKey];
  };
  const isSubRowDisabled = (rowData, parentRowData) => {
    const rowKey = get(parentRowData, props.rowKey);
    const subRowKey = get(rowData, props.subRowKey);
    return props.disableSelectedKeys.includes(rowKey) || props.disableSelectedKeys.includes(subRowKey);
  };
  const isRowSelected = (rowData) => {
    const rowKey = get(rowData, props.rowKey);
    return selectedRowKeys.value[rowKey] || false;
  };

  const toggleAllRowsSelection = (checked) => {
    if (checked) {
      const rowKeysSelected = {};
      const rowDataSelected = [];
      let subRowKeysSelected = {};
      const subRowDataSelected = [];

      props.data.forEach((rowData) => {
        const rowKey = get(rowData, props.rowKey);
        rowKeysSelected[rowKey] = true;
        rowDataSelected.push(rowData);

        const { subRowKeys, subRowData } = selectSubRows(rowData);

        if (subRowData?.length > 0) {
          subRowKeysSelected = {
            ...subRowKeysSelected,
            ...subRowKeys,
          };
          subRowDataSelected.push(...subRowData);
        }
      });

      selectedRowKeys.value = rowKeysSelected;
      selectedRowData.value = rowDataSelected;
      selectedSubRowKeys.value = subRowKeysSelected;
      selectedSubRowData.value = subRowDataSelected;
    } else {
      selectedRowKeys.value = {};
      selectedRowData.value = [];
      selectedSubRowKeys.value = {};
      selectedSubRowData.value = [];
    }

    emitRowUpdate();
    emitSubRowUpdate();
  };

  const toggleRowSelection = ({ checked, data }) => {
    if (checked) {
      const rowKey = get(data, props.rowKey);
      selectedRowKeys.value[rowKey] = true;
      selectedRowData.value.push(data);

      if (props.subRowsTableSelectable && rowCanExpand(data)) {
        const { subRowKeys, subRowData } = selectSubRows(data);
        selectedSubRowKeys.value = {
          ...selectedSubRowKeys.value,
          ...subRowKeys,
        };
        selectedSubRowData.value.push(...subRowData);
        emitSubRowUpdate();
      }
    } else {
      const rowKey = get(data, props.rowKey);
      delete selectedRowKeys.value[rowKey];
      const index = selectedRowData.value.findIndex((row) => get(row, props.rowKey) === rowKey);
      if (index !== -1) {
        selectedRowData.value.splice(index, 1);
      }

      if (props.subRowsTableSelectable && rowCanExpand(data)) {
        const subRowKeys = JSON.parse(JSON.stringify(selectedSubRowKeys.value));
        const subRowData = JSON.parse(JSON.stringify(selectedSubRowData.value));
        let subRowKeysChanged = false;

        data.subRows?.forEach((subData) => {
          const subRowKey = get(subData, props.subRowKey);
          if (subRowKeys[subRowKey]) {
            subRowKeysChanged = true;
            delete subRowKeys[subRowKey];
            const subIndex = subRowData?.findIndex((subRow) => get(subRow, props.subRowKey) === subRowKey);

            if (subIndex !== -1) {
              subRowData.splice(subIndex, 1);
            }
          }
        });

        if (subRowKeysChanged) {
          selectedSubRowKeys.value = subRowKeys;
          selectedSubRowData.value = subRowData;
          emitSubRowUpdate();
        }
      }
    }

    emitRowUpdate();
  };
  const toggleSubRowSelection = ({ checked, data }, parentRowData) => {
    if (checked) {
      const subRowKey = get(data, props.subRowKey);

      selectedSubRowKeys.value[subRowKey] = true;
      selectedSubRowData.value.push(data);

      if (!isRowSelected(parentRowData)) {
        const rowKey = get(parentRowData, props.rowKey);
        selectedRowKeys.value[rowKey] = true;
        selectedRowData.value.push(parentRowData);
        emitRowUpdate();
      }
    } else {
      const subRowKey = get(data, props.subRowKey);
      delete selectedSubRowKeys.value[subRowKey];
      const index = selectedSubRowData.value.findIndex((subRow) => get(subRow, props.subRowKey) === subRowKey);
      if (index !== -1) {
        selectedSubRowData.value.splice(index, 1);
      }

      if (isRowSelected(parentRowData)
        && !selectedSubRowData.value.some((subRow) => isSubRowSelected(subRow))
      ) {
        const rowKey = get(parentRowData, props.rowKey);
        delete selectedRowKeys.value[rowKey];
        const rowIndex = selectedRowData.value.findIndex((row) => get(row, props.rowKey) === rowKey);
        if (rowIndex !== -1) {
          selectedRowData.value.splice(rowIndex, 1);
        }
      }
    }

    emitSubRowUpdate();
  };

  const selectedRowsCount = computed(() => {
    return selectedRowData.value.length;
  });
  const selectedSubRowsCount = computed(() => {
    return selectedSubRowData.value.length;
  });
  const displayActionBar = computed(() => {
    return !!(slots['sb-table-action-bar'] && (selectedRowsCount.value > 0 || selectedSubRowsCount.value > 0 || props?.showActionBar));
  });
  const allRowsSelected = computed(() => {
    if (props.data.length === 0 || selectedRowsCount.value === 0) {
      return false;
    }

    // Count how many disabled keys actually exist in the data
    const actualDisabledCount = props?.disableSelectedKeys
      ? props.disableSelectedKeys.filter((disabledKey) => {
        return props.data.some((row) => get(row, props.rowKey) === disabledKey);
      }).length
      : 0;

    return selectedRowsCount.value + actualDisabledCount >= props.data.length;
  });
  const someRowsSelected = computed(() => {
    return !!((selectedRowsCount.value > 0 || selectedSubRowsCount.value > 0) && !allRowsSelected.value);
  });

  const isRowDisabled = (data, parentData = false) => {
    const rowKeyProperty = parentData ? props.subRowKey : props.rowKey;
    const actualRowKey = get(data, rowKeyProperty);
    let isRowDisabled = false;

    if (props.disableSelectedKeys?.includes(actualRowKey)) {
      isRowDisabled = true;
    } else if (props.selectable === 'single') {
      if (selectedRowsCount.value > 0) {
        if (parentData) {
          isRowDisabled = !isSubRowSelected(data);
        } else {
          isRowDisabled = !isRowSelected(data);
        }
      }
    }

    return isRowDisabled;
  };

  return {
    selectedRowKeys,
    selectedRowData,
    selectedSubRowKeys,
    selectedSubRowData,
    toggleAllRowsSelection,
    toggleRowSelection,
    toggleSubRowSelection,
    isRowSelected,
    isRowDisabled,
    isSubRowSelected,
    isSubRowDisabled,
    displayActionBar,
    allRowsSelected,
    selectedRowsCount,
    someRowsSelected,
    selectedSubRowsCount,
  };
}
